import React from 'react';
import PropTypes from 'prop-types';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { ContextStore } from '../store/contextStore';
import { Box, Button, Divider, CircularProgress, Grid, List, Skeleton, Typography, Chip, Popover, IconButton, Tooltip } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { DataGridPro, getGridDateOperators } from '@mui/x-data-grid-pro';
import clsx from 'clsx';
import { useSnackbar } from 'notistack';
import { useState, useEffect, useMemo, useContext, useCallback, forwardRef, useImperativeHandle } from 'react';
import { useHistory } from 'react-router';
import EditIcon from '@mui/icons-material/BorderColorOutlined';
import SupervisedUserCircleIcon from '@mui/icons-material/SupervisedUserCircle';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import LinkIcon from '@mui/icons-material/Link';
import DeleteIconButton from './DeleteIconButton';
import PersonIcon from '@mui/icons-material/Person';
import { Link } from 'react-router-dom';
import { formatNewUserManagerPushURL } from '../trips/utils';

import { DataAPIContext, GET_LIST, GET_ONE, CREATE } from '../api';
import { useIsWidthUp } from '../utils';

import { serverErrorMessage } from '../utils/errors';

import AutoRefreshGridFooter, { useAutoRefresh } from './AutoRefreshGridFooter';
import { serverDateOperators, toApiFilter } from './gridDateFilters';
import useConfirmDialog from './useConfirmDialog';

const GridSkeleton = (props) => {
    const animation = props.animation || 'wave';
    return (
        <div>
            <Typography variant="h4">
                <Skeleton variant="text" animation={animation} />
            </Typography>
            <Grid container spacing={1}>
                <Grid item xs={12} sm={3}>
                    <Skeleton variant="rect" animation={animation} />
                </Grid>
                <Grid item xs={12} sm={3}>
                    <Skeleton variant="rect" animation={animation} />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <Skeleton variant="rect" animation={animation} />
                </Grid>
                <Grid item xs={12} sm={3}>
                    <Skeleton variant="rect" animation={animation} />
                </Grid>
                <Grid item xs={12} sm={3}>
                    <Skeleton variant="rect" animation={animation} />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <Skeleton variant="rect" animation={animation} />
                </Grid>
                <Grid item xs={12} sm={3}>
                    <Skeleton variant="rect" animation={animation} />
                </Grid>
                <Grid item xs={12} sm={3}>
                    <Skeleton variant="rect" animation={animation} />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <Skeleton variant="rect" animation={animation} />
                </Grid>
            </Grid>
        </div>
    );
};

GridSkeleton.propTypes = {
    animation: PropTypes.string,
};

/** Pulsing dots for a grid total that is still being counted. CP-8288. */
const PendingCountDots = () => (
    <span aria-hidden="true" style={{ display: 'inline-flex' }}>
        {[0, 1, 2].map((i) => (
            <Box
                key={i}
                component="span"
                sx={{
                    display: 'inline-block',
                    animation: 'cp8288Dot 1.2s ease-in-out infinite',
                    animationDelay: `${i * 0.2}s`,
                    '@keyframes cp8288Dot': {
                        '0%, 80%, 100%': { opacity: 0.25 },
                        '40%': { opacity: 1 },
                    },
                }}
            >
                .
            </Box>
        ))}
    </span>
);

const useStyles = makeStyles((theme) => ({
    sortButton: {
        minWidth: theme.spacing(4),
        minHeight: theme.spacing(4),
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1),
        padding: theme.spacing(1),
        marginRight: theme.spacing(1),
    },
    recordList: {
        border: 'none !important',
        borderBottom: '1px solid #d8d8d8 !important',
        height: `calc(${theme.cp.contentHeight} - 10rem) !important`,
        marginBottom: '-2rem',
        '&.MuiDataGrid-root .MuiDataGrid-cell:focus-within': {
            outline: 'none !important',
        },
    },
    itemListRoot: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(12),
        overflowY: 'scroll',
        flexGrow: 1,
    },
    itemListRootNoMarginBottom: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(4),
        overflowY: 'scroll',
        flexGrow: 1,
    },
    withBorder: {
        border: '1px solid #d7d7d7',
        borderRadius: '15px',
    },
    mobileWrapper: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
    },
    paginationButtons: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingBottom: '1.5rem',
    },
}));



export const onPretendToBeUser = (ev, user, finalizeLogin, resource, dataAPI, enqueueSnackbar) => {
    ev.preventDefault();
    ev.stopPropagation();
    console.log('pretending to be', user);
    if (window.confirm(`Are you sure that you want to be logged in as ${user.email} (ID: ${user.id})?\nYour current dashboard session will be replaced and you will assume ${user.email}'s access level until log out.`)) {
        // A 403 here is an answer (no user.pretend, a session that has not passed two-step sign-in…), shown in
        // the backend's words; it must not sign the impersonator out.
        dataAPI(CREATE, resource, { urlSuffix: 'pretend/', keepSessionOn403: true, data: { id: user.id } })
            .then(res => {
                if (!res) { return; } // a 401: the data provider is signing out
                console.log('pretend res', res.statusCode, res.data);
                if ([200, 201].indexOf(+res.statusCode) >= 0) {
                    finalizeLogin(res.data).then(() => {
                        enqueueSnackbar(`Switched session to user ${user.email}`, { variant: 'success' });
                        window.location.reload();
                    });
                } else {
                    enqueueSnackbar(serverErrorMessage(res, `Error switching session: ${res.statusCode}`), { variant: 'error' });
                }
            });
    }
    return false;
}

const MobileUserRow = (props) => {
    const [confirmDelete, confirmDeleteDialog] = useConfirmDialog();
    const {
        row,
        idx,
        rowsLength,
        roleMap,
        hasPerm,
        contextFinalizeLogin,
        resource,
        dataAPI,
        enqueueSnackbar,
        currentTab,
        onRemoveUserFromTrip,
        onDeleteUser,
        roleCellBackgroundColor,
        showRoleChips = true,
        searchFilterList,
        recordListFilters,
        trip,
        setTourUserId,
        history,
    } = props;

    const [tagsAnchorEl, setTagsAnchorEl] = useState(null);

    const user = row.user || row;
    if (!user) return null;

    const displayName = user.display_name || user.email || 'N/A';
    const showSecondaryEmail = user.display_name && user.email && user.email !== user.display_name;

    const handleTagsClick = (event) => {
        setTagsAnchorEl(event.currentTarget);
    };
    const handleTagsClose = () => {
        setTagsAnchorEl(null);
    };
    const openTagsPopover = Boolean(tagsAnchorEl);
    const tagsPopoverId = openTagsPopover ? `tags-popover-${row.id}` : undefined;

    const handleCopyLink = () => {
        if (row.branchio_link) {
            navigator.clipboard.writeText(row.branchio_link)
                .then(() => enqueueSnackbar('Invitation link copied to clipboard!', { variant: 'success' }))
                .catch(err => enqueueSnackbar('Failed to copy link: ' + err, { variant: 'error' }));
        } else {
            enqueueSnackbar('No invitation link available for this user.', { variant: 'info' });
        }
    };

    const handleRemoveUser = async () => {
        if (trip) {
            if (props.skipDeleteConfirmation) {
                onRemoveUserFromTrip && onRemoveUserFromTrip(row.id);
            } else if (await confirmDelete({ body: `Are you sure you want to remove ${displayName} from this trip?`, okButtonText: 'Remove', destructive: true })) {
                onRemoveUserFromTrip && onRemoveUserFromTrip(row.id);
            }
        } else if (onDeleteUser) {
            if (props.skipDeleteConfirmation) {
                onDeleteUser([row.id]);
            } else if (await confirmDelete({ body: `Are you sure you want to delete ${displayName}?` })) {
                onDeleteUser([row.id]);
            }
        }
    };

    // CP-5545: the delete shows wherever it applies and is disabled (not hidden) without the delete permission.
    const showTripRemoveButton = Boolean(trip);
    const showRecordDeleteButton = Boolean(!trip && onDeleteUser);
    const canRemoveOrDelete = trip ? hasPerm('tour_participant', 'delete') : hasPerm(resource, 'delete');

    let roleChipLabel = null;
    let roleChipColor = null;

    if (user.roles && user.roles.length > 0 && roleMap[user.roles[0]]) {
        roleChipLabel = roleMap[user.roles[0]].name;
        roleChipColor = roleCellBackgroundColor && roleCellBackgroundColor[user.roles[0]];
    } else if (showRoleChips && user.agroups && user.agroups.some(agroup => agroup.is_guest_group || agroup.name === 'Guests')) {
        roleChipLabel = 'Guest';
        roleChipColor = '#E0E0E0'; // A generic grey color for Guest
    }

    return (
        <>
            {confirmDeleteDialog}
            <Grid container spacing={1} p={2} alignItems="center">
                {/* First row: Name and Chip */}
                <Grid item xs={12} container spacing={1} alignItems="center" wrap="nowrap">
                    <Grid item xs style={{ minWidth: 0 }}>
                        <Typography style={{ wordWrap: 'break-word', whiteSpace: 'normal' }} noWrap title={displayName}>
                            {displayName}
                        </Typography>
                    </Grid>
                    {roleChipLabel && (
                        <Grid item>
                            <Chip
                                style={{ backgroundColor: roleChipColor || undefined }}
                                size="small"
                                label={roleChipLabel}
                                sx={{ borderRadius: '4px' }}
                            />
                        </Grid>
                    )}
                </Grid>

                {showSecondaryEmail && (
                    <Grid item xs={12}>
                        <Typography variant="body2" color="textSecondary" noWrap title={user.email}>
                            {user.email}
                        </Typography>
                    </Grid>
                )}

                {user.phone && (
                    <Grid item xs={12}>
                        <Typography variant="body2" color="textSecondary" noWrap title={user.phone}>
                            {user.phone}
                        </Typography>
                    </Grid>
                )}

                {/* Action Icons */}
                <Grid item xs={12} container justifyContent="flex-start" spacing={0.5} alignItems="center">
                    {hasPerm('user', 'read') && (
                        <Grid item>
                            <Tooltip title="User profile">
                                <IconButton onClick={() => history.push(`/user/${row.id}`)} aria-label="User profile" size="small">
                                    <PersonIcon fontSize="small" color="primary" />
                                </IconButton>
                            </Tooltip>
                        </Grid>
                    )}
                    {!trip && hasPerm('user', 'read') && (
                        <Grid item>
                            <Tooltip title="Edit user">
                                <IconButton 
                                    onClick={() => history.push(`/user/${row.id}/edit`)} 
                                    aria-label="Edit user" 
                                    size="small"
                                >
                                    <EditIcon fontSize="small" color="primary" />
                                </IconButton>
                            </Tooltip>
                        </Grid>
                    )}
                    {trip && (
                        <Grid item>
                            <Tooltip title="Edit trip user">
                                <IconButton
                                    component={Link}
                                    to={formatNewUserManagerPushURL(searchFilterList, recordListFilters, trip.id, row.id)}
                                    onClick={(ev) => {
                                        ev.preventDefault();
                                        ev.stopPropagation();
                                        history.push(formatNewUserManagerPushURL(searchFilterList, recordListFilters, trip.id, row.id));
                                        setTourUserId(null);
    
                                        setTimeout(() => {
                                            setTourUserId(row.id);
                                        }, 100);
                                    }}
                                    aria-label="Edit trip user"
                                    size="small"
                                >
                                    <EditIcon fontSize="small" color="primary" />
                                </IconButton>
                            </Tooltip>
                        </Grid>
                    )}
                    {hasPerm('user', 'pretend') && currentTab === 1 && (
                        <Grid item>
                            <Tooltip title="Impersonate user">
                                <IconButton onClick={ev => onPretendToBeUser(ev, user, contextFinalizeLogin, resource, dataAPI, enqueueSnackbar)} aria-label="Pretend to be user" size="small">
                                    <SupervisedUserCircleIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Grid>
                    )}
                    {row.tags && row.tags.length > 0 && (
                        <Grid item>
                            <Tooltip title="Show tags">
                                <IconButton onClick={handleTagsClick} aria-describedby={tagsPopoverId} aria-label="Show tags" size="small">
                                    <LocalOfferOutlinedIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Popover
                                id={tagsPopoverId}
                                open={openTagsPopover}
                                anchorEl={tagsAnchorEl}
                                onClose={handleTagsClose}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'left',
                                }}
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'left',
                                }}
                            >
                                <Box p={2} display="flex" flexDirection="column" gap={1}>
                                    <Typography variant="subtitle2">Tags:</Typography>
                                    {row.tags.map(tag => (
                                        <Chip key={tag.id || tag.name} label={tag.name} size="small" sx={{ borderRadius: '4px' }} />
                                    ))}
                                </Box>
                            </Popover>
                        </Grid>
                    )}
                    {row.branchio_link && (
                        <Grid item>
                            <Tooltip title="Copy invitation link">
                                <IconButton onClick={handleCopyLink} aria-label="Copy invitation link" size="small">
                                    <LinkIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Grid>
                    )}
                    {(showTripRemoveButton || showRecordDeleteButton) && (
                        <Grid item>
                            <DeleteIconButton
                                title={trip ? 'Remove user from trip' : 'Delete user'}
                                allowed={canRemoveOrDelete}
                                onClick={handleRemoveUser}
                            />
                        </Grid>
                    )}
                </Grid>
            </Grid>
            {idx < rowsLength - 1 ? <Divider /> : null}
        </>
    );
};

MobileUserRow.propTypes = {
    row: PropTypes.object.isRequired,
    idx: PropTypes.number.isRequired,
    rowsLength: PropTypes.number.isRequired,
    roleMap: PropTypes.object.isRequired,
    hasPerm: PropTypes.func.isRequired,
    contextFinalizeLogin: PropTypes.func.isRequired,
    resource: PropTypes.string.isRequired,
    dataAPI: PropTypes.func.isRequired,
    enqueueSnackbar: PropTypes.func.isRequired,
    currentTab: PropTypes.number,
    onRemoveUserFromTrip: PropTypes.func,
    onDeleteUser: PropTypes.func,
    roleCellBackgroundColor: PropTypes.object,
    showRoleChips: PropTypes.bool,
    skipDeleteConfirmation: PropTypes.bool,
    searchFilterList: PropTypes.array,
    recordListFilters: PropTypes.object,
    trip: PropTypes.object,
    setTourUserId: PropTypes.func,
    history: PropTypes.object,
};

const RecordListPro = forwardRef((props, ref) => {
    const history = useHistory();
    const searchParams = new URLSearchParams(history.location.search);
    const [calledFromResetPage, setCalledFromResetPage] = useState(false);

    const { resource } = props;
    const { roleMap, hasPerm, finalizeLogin: contextFinalizeLogin } = useContext(ContextStore);
    const roleCellBackgroundColor = props.roleCellBackgroundColor || {
        10: '#D8D8D8',
        78: '#DAF3E5',
        11: '#E4E4F9',
    };

    const classes = useStyles();

    const dataAPI = useContext(DataAPIContext);
    const { enqueueSnackbar } = useSnackbar();
    const isSmallScreen = !useIsWidthUp('sm');

    const [total, setTotal] = useState(null);
    const [rows, setRows] = useState([]);
    const [page, setPage] = useState(props.page || searchParams.get('page') || 1);
    const [perPage, setPerPage] = useState(props.perPage || searchParams.get('pageSize') || 25);
    const defaultSortModel = props.sortModel || [{ field: 'id', sort: 'asc' }];
    const isControlledSort = typeof props.onSortModelChange === 'function';
    const [internalSortModel, setInternalSortModel] = useState(defaultSortModel);
    const sortModel = isControlledSort ? (props.sortModel ?? defaultSortModel) : internalSortModel;
    const [loading, setLoading] = useState(false);
    const [filterList, setFilterList] = useState(props.initialState?.filter?.filterModel?.items || []);
    const [asyncCountHandle, setAsyncCountHandle] = useState(null);
    // True while `total` is the synthesized page*size+1 placeholder used to keep
    // the Next button alive before the async count lands. The footer then shows
    // "1–25 of …" (animated) instead of presenting the placeholder as a real
    // total. CP-8288.
    const [totalIsEstimate, setTotalIsEstimate] = useState(false);
    const rowsPerPageOptions = props.rowsPerPageOptions || [10, 25, 50, 100];
    const totPages = Math.floor(total / perPage) + (total % perPage ? 1 : 0);

    const gridInitialState = React.useMemo(() => ({
        ...props.initialState,
        pagination: { paginationModel: { pageSize: perPage } },
    }), [props.initialState, perPage]);

    const orderingField = props.orderingField || 'sequence';
    const isReorderingAllowed = filterList?.length === 0 && sortModel?.length === 1 && sortModel[0]?.field === orderingField;

    // Mirrors `loading` for the auto-refresh poll, as a ref because the poll runs in
    // a setInterval closure; see useRefreshTrigger for why ticks wait on it.
    const fetchInFlightRef = React.useRef(false);
    const { refreshTrigger, refreshTriggerValue } = useAutoRefresh(fetchInFlightRef);

    useEffect(() => {
        const previous_page_size = searchParams.get('pageSize');
        const previous_page = searchParams.get('page');

        if (!props.disablePageUrlData) {
            if (previous_page_size) {
                setPerPage(parseInt(previous_page_size));
            }
            if (previous_page) {
                setPage(parseInt(previous_page));
            }
        }

    }, []);

    useEffect(() => {
        onPaginationModelChange({ page: 0, pageSize: perPage });
    }, [props.query, props.pageReset]);

    useEffect(() => {
        if (props.staticOptions) {
            setTotal(props.staticOptions.length);
        }
    }, [props.staticOptions]);

    useEffect(() => {
        props.setIsLoading?.(loading)

    }, [loading]);

    const components = {
        ...props.slots,
        NoRowsOverlay: () => (
            <Box display="flex" height="100%" alignItems="center" justifyContent="center" flexDirection="column" gap="1rem">
                <div>
                    <svg width="86" height="87" viewBox="0 0 86 87" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <g clipPath="url(#clip0_57_14421)">
                            <path d="M43 86.5C66.7482 86.5 86 67.2482 86 43.5C86 19.7518 66.7482 0.5 43 0.5C19.2518 0.5 0 19.7518 0 43.5C0 67.2482 19.2518 86.5 43 86.5Z" fill="#EEF5FA" />
                            <path d="M77.2072 8.36279H14.2266V82.072H77.2072V8.36279Z" fill="#AEDCFF" />
                            <path d="M74.4904 6.64613H11.5098V80.3553H74.4904V6.64613Z" fill="white" />
                            <line x1="30.7071" y1="32" x2="56" y2="57.2929" stroke="#007AFF" strokeLinecap="round" />
                            <line x1="0.5" y1="-0.5" x2="36.2696" y2="-0.5" transform="matrix(-0.707107 0.707107 0.707107 0.707107 56 32)" stroke="#007AFF" strokeLinecap="round" />
                        </g>
                        <defs>
                            <clipPath id="clip0_57_14421">
                                <rect width="86" height="86" fill="white" transform="translate(0 0.5)" />
                            </clipPath>
                        </defs>
                    </svg>
                </div>
                <Typography fontWeight={400} fontSize={16}>
                    {props.noMatchesLabel ? props.noMatchesLabel : 'No records'}
                </Typography>
                {props.createNewButton && props.createNewButton}
            </Box>
        ),
    };
    if (!components.Toolbar) {
        components.Toolbar = props.toolbar;
    }

    const onRowClick = (params, ev) => {
        props.onRowClick &&
            props.onRowClick(params, ev, {
                setLoading: setLoading,
            });
    };

    const onPaginationModelChange = (params) => {
        var currentPage = 0;
        var pageSize = params.pageSize;
        if (calledFromResetPage) {
            setCalledFromResetPage(false);
        } else {
            currentPage = params.page;
            pageSize = params.pageSize;
        }
        var parameters = history.location.search.split('&');
        var tab = undefined;
        parameters.forEach(param => {
            if (param.includes('tab')) {
                tab = param.split('=')[1];
            }
        });

        if (perPage != pageSize) {
            onPageSizeChange(pageSize);
        } else {
            onNewPageChange(currentPage + 1, pageSize, tab);
        }
    }

    const resetPage = () => {
        onPaginationModelChange({ page: 0, pageSize: perPage });
        setCalledFromResetPage(true);
    }

    useImperativeHandle(ref, () => ({
        resetPage,
    }));

    const onNewPageChange = (newPage, pageSize, tab) => {
        setPage(newPage);

        if (props.setLastDataGridPage) {
            props.setLastDataGridPage(newPage);
        }

        if (props.disablePageUrlData) {
            return;
        }

        const searchParams = new URLSearchParams(history.location.search);
        searchParams.set('page', newPage);
        searchParams.set('pageSize', pageSize);
        if (tab) {
            searchParams.set('tab', tab);
        }
        history.push({ search: searchParams.toString() });
    };

    const onPageChange = (params) => {
        // adjust to 1-indexed page numbering for mobile pagination
        setPage(params + 1);

        if (props.setLastDataGridPage) {
            props.setLastDataGridPage(params);
        }

        if (props.disablePageUrlData) {
            return;
        }

        const searchParams = new URLSearchParams(history.location.search);
        searchParams.set('page', params + 1);
        history.push({ search: searchParams.toString() });
    };

    const onPageSizeChange = (params) => {
        setPerPage(params);
        setPage(1);

        if (props.setLastDataGridPerPage) {
            props.setLastDataGridPerPage(params);
        }
        if (props.setLastDataGridPage) {
            props.setLastDataGridPage(1);
        }
        if (props.disablePageUrlData) {
            return;
        }

        const searchParams = new URLSearchParams(history.location.search);
        searchParams.set('page', 1);
        searchParams.set('pageSize', params);

        history.push({ search: searchParams.toString() });
    };

    const onSortModelChange = (params) => {
        if (isControlledSort) {
            props.onSortModelChange(params);
            return;
        }
        if (JSON.stringify(params) !== JSON.stringify(internalSortModel)) {
            setInternalSortModel(params);
        }
    };
    const convertFilters = (flist) => {
        return (flist || [])
            .map((f) => {
                try {
                    return toApiFilter(f, props.columns);
                } catch (e) {
                    console.warning('RecordList.convertFilters: filter=', f, ', exception=', e, ', columns=', props.columns);
                    return null;
                }
            })
            .filter((e) => !!e);
    };
    // The exact filter payload the list request sends: the grid's own column
    // filters plus the caller's static ones. Computed once and reported to the
    // parent, because a bulk action derived from this grid (export, select-all)
    // MUST resolve the same rows the list is showing. When the parent only knew
    // its own `apiFilters`, a grid filtered to 24,620 rows still reported and
    // exported all 735,530.
    const effectiveFilters = useMemo(
        () => [...convertFilters(filterList), ...(props.apiFilters || [])],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [filterList, props.apiFilters],
    );

    const { onEffectiveFiltersChange } = props;
    useEffect(() => {
        if (onEffectiveFiltersChange) {
            onEffectiveFiltersChange(effectiveFilters);
        }
    }, [effectiveFilters, onEffectiveFiltersChange]);

    const onFilterChange = useCallback((filterModel) => {
        if (filterModel && filterModel.items) {
            // const fl = filterModel.items.filter((x) => (typeof x.value !== "undefined"));
            const fl = filterModel.items;
            setFilterList(fl);
        } else {
            setFilterList([]);
        }
    });

    const _sortModel2sort = (sm) => {
        if (sm && sm.length > 0) {
            return { field: sm[0].field, order: sm[0].sort };
        } else if (defaultSortModel && defaultSortModel.length > 0) {
            return { field: defaultSortModel[0].field, order: defaultSortModel[0].sort };
        }
    };
    const handleReorder = (p, idx, nextIdx, direction, ev) => {
        ev.stopPropagation();
        const order = sortModel[0]?.sort; // either 'asc' or 'desc'
        if (nextIdx === perPage || (idx === 0 && direction === 'up')) {
            let rowOrderMap = {
                [p.row.id]: p.row.sequence,
                direction: direction,
            };
            props.onRowOrderUpdate && props.onRowOrderUpdate(rowOrderMap);
            return;
        }
        const nextId = p.api.getRowIdFromRowIndex(nextIdx);
        const nextRow = p.api.getRow(nextId);
        const [a, b] = [nextRow.sequence, p.row.sequence];
        const rowOrderMap = {
            [p.row.id]: a,
            [nextRow.id]: b,
            direction: direction,
        };
        const newRows = rows
            .map((r) => {
                if (rowOrderMap[r.id]) {
                    return { ...r, sequence: rowOrderMap[r.id] };
                } else {
                    return r;
                }
            })
            .sort((a, b) => (order === 'desc' ? b.sequence - a.sequence : a.sequence - b.sequence));
        setRows(newRows);
        props.onRowsChanged && props.onRowsChanged(newRows);
        props.onRowOrderUpdate && props.onRowOrderUpdate(rowOrderMap);
    };

    const reorderType = {
        type: 'number',
        width: 150,
        renderCell: (p) => {
            const upDisabled = props.staticOptions ? p.api.getRowIndex(p.id) === 0 && page === 1 : !isReorderingAllowed || (p.api.getRowIndex(p.id) === 0 && page === 1);
            const downDisabled = props.staticOptions ? p.api.getRowIndex(p.id) + 1 === p.api.getRowsCount() : !isReorderingAllowed || (page > 1 ? (page - 1) * perPage : 0) + (p.api.getRowIndex(p.id) + 1) === p.api.getRowsCount();
            const title = isReorderingAllowed ? null : `To reorder rows remove filters and sort the table by the '${orderingField}' field`;
            return (
                <div style={{ transform: 'scale(0.85)', gap: '0.25rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                    <Button disabled={upDisabled} title={title} onClick={(ev) => handleReorder(p, p.api.getRowIndex(p.id), p.api.getRowIndex(p.id) - 1, 'up', ev)} variant="outlined" size="small" className={classes.sortButton}>
                        <ArrowUpwardIcon color={upDisabled ? 'inherit' : 'primary'} />
                    </Button>
                    <Button disabled={downDisabled} title={title} onClick={(ev) => handleReorder(p, p.api.getRowIndex(p.id), p.api.getRowIndex(p.id) + 1, 'down', ev)} variant="outlined" size="small" className={classes.sortButton}>
                        <ArrowDownwardIcon color={downDisabled ? 'inherit' : 'primary'} />
                    </Button>
                    <div>
                        <Typography>{p.row.sequence}</Typography>
                    </div>
                </div>
            );
        },
    };

    const columns = props.columns
        // .filter(c => ((c.type !== 'reorder') || isReorderingAllowed))
        .map((c) => {
            if (c.type === 'reorder') {
                return { ...c, ...reorderType };
            } else if ((c.type === 'date' || c.type === 'dateTime') && !c.filterOperators) {
                return { ...c, filterOperators: serverDateOperators(getGridDateOperators, c.type) };
            } else {
                return c;
            }
        });

    useEffect(() => {
        if (props.staticOptions) {
            setTotal(props.staticOptions.length);
            return;
        }
        let active = true;
        let ac = new AbortController();

        (async () => {
            setLoading(true);
            fetchInFlightRef.current = true;
            const params = {
                signal: ac.signal,
                convert: props.convert,
                pagination: { page, perPage },
                sort: _sortModel2sort(sortModel),
                filter: {
                    query: props.query,
                    filters: JSON.stringify(effectiveFilters),
                    ...(props.extraQueryParams || {}),
                },
                urlSuffix: props.urlSuffix,
            };
            try {
                const res = await dataAPI(GET_LIST, resource, params);
                if (!active) {
                    return;
                }
                if (res.statusCode === 500) {
                    enqueueSnackbar('Sorry, our server is under maintenance, for more information please contact support', { variant: 'error' });
                }
                if (res.statusCode === 429) {
                    // The server capped concurrent runs of this list (Agent Metrics
                    // does this for its ClickHouse report); the body is an empty page.
                    enqueueSnackbar('This report is busy right now. Please try again in a few seconds.', { variant: 'warning' });
                }
                if (res.total < 0) {
                    const num_to_show_on_page = (res.data || []).length;
                    const prev_pages_total = (page - 1) * perPage;
                    if ((res.data || []).length >= perPage) {
                        setTotal(prev_pages_total + num_to_show_on_page + 1); // set to something bigger than the current page size to show the paginator controls
                        setTotalIsEstimate(true);
                    }
                    else {
                        // A short page pins the exact total even without a count.
                        setTotal(prev_pages_total + num_to_show_on_page);
                        setTotalIsEstimate(false);
                    }
                    if (res.async_count_handle) {
                        setAsyncCountHandle(res.async_count_handle);
                    }
                } else {
                    setTotal(res.total);
                    setTotalIsEstimate(false);
                }
                const nextRows = props.transformRows ? props.transformRows(res.data || []) : (res.data || []);
                setRows(nextRows);
                setLoading(false);
                props.onRowsChanged && props.onRowsChanged(nextRows);
                props.onListFetchComplete?.();
            } catch (e) {
                if (e.name === 'AbortError') {
                    return;
                }
                setLoading(false);
                props.onListFetchComplete?.();
                throw e;
            } finally {
                // Only the current effect instance owns the flag: a superseded fetch
                // settling late must not clear it under the fetch that replaced it.
                if (active) {
                    fetchInFlightRef.current = false;
                }
            }
        })();

        return () => {
            active = false;
            ac.abort();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataAPI, resource, page, perPage, sortModel, filterList, props.query, props.deps, props.hiddenAPIFilters, props.apiFilters, props.extraQueryParams, props.transformRows, refreshTriggerValue]);

    useEffect(() => {
        if (!asyncCountHandle) { return; }
        let active = true;
        const refreshAsyncCount = () => {
            dataAPI(GET_ONE, resource, { urlSuffix: 'async_count/', filter: { handle: asyncCountHandle } }).then((res) => {
                if (!active) { return; }
                if (res.statusCode === 200) {
                    if (res.data >= 0) {
                        setTotal(res.data);
                        setTotalIsEstimate(false);
                        setAsyncCountHandle(null);
                        clearInterval(tm);
                    }
                } else {
                    console.error(res);
                }
            });
        };
        const tm = setInterval(refreshAsyncCount, 1000);
        refreshAsyncCount();
        return () => {
            active = false;
            clearInterval(tm);
        };
    }, [dataAPI, resource, sortModel, filterList, props.query, props.deps, props.hiddenAPIFilters, props.apiFilters, props.extraQueryParams, asyncCountHandle, refreshTriggerValue]);

    const onEditCellChangeCommitted = (change, ev) => {
        props.onEditCellChangeCommitted && props.onEditCellChangeCommitted(change, ev);
    };

    if (rows === null) {
        return <GridSkeleton />;
    } else {
        const filteredRows = props.staticOptions
            ? props.staticOptions
            : rows.filter((row) => {
                return (props.deletedList || []).indexOf(row.id) < 0;
            });


        const MobileRowComponent = props.mobileRowComponent || MobileUserRow;
        if (isSmallScreen && MobileRowComponent) {
            const hasPreviousPage = page > 1;
            const hasNextPage = page * perPage < total;
            const goToPreviousPage = () => hasPreviousPage && onPageChange(page - 2); // page is 1-indexed, onPageChange expects 0-indexed
            const goToNextPage = () => hasNextPage && onPageChange(page); // page is 1-indexed, onPageChange expects 0-indexed for next page
            const mobileBlockingLoad = loading || !!props.relatedLoading;

            return (
                <div className={clsx(classes.mobileWrapper)} style={{ height: props.limitHeightForMobile ? props.limitHeightForMobile : "100%" }}>
                    {mobileBlockingLoad ? (
                        <Box position="fixed" top="50%" left="50%" style={{ transform: 'translate(-50%, -50%)', zIndex: 10000 }}>
                            <CircularProgress color="primary" />
                        </Box>
                    ) : filteredRows.length > 0 ? (
                        <>
                            <Grid className={clsx(hasPreviousPage || hasNextPage ? classes.itemListRootNoMarginBottom : classes.itemListRoot, !props.disableListBorder && classes.withBorder)}>
                                <List >{filteredRows.map((row, idx) => (
                                    <MobileRowComponent
                                        key={row.id || idx} // Ensure a unique key
                                        row={row}
                                        idx={idx}
                                        rowsLength={filteredRows.length}
                                        roleMap={roleMap}
                                        hasPerm={hasPerm}
                                        contextFinalizeLogin={contextFinalizeLogin}
                                        resource={resource}
                                        dataAPI={dataAPI}
                                        enqueueSnackbar={enqueueSnackbar}
                                        currentTab={props.currentTab} // Pass currentTab if used by MobileRowComponent
                                        onRemoveUserFromTrip={props.onRemoveUserFromTrip}
                                        onDeleteUser={props.onDeleteUser}
                                        roleCellBackgroundColor={roleCellBackgroundColor}
                                        showRoleChips={props.showRoleChips}
                                        skipDeleteConfirmation={props.skipDeleteConfirmation}
                                        searchFilterList={props.searchFilterList}
                                        recordListFilters={props.recordListFilters}
                                        trip={props.trip}
                                        setTourUserId={props.setTourUserId}
                                        history={history}
                                    />
                                ))}</List>
                            </Grid>
                            {hasPreviousPage || hasNextPage ? (
                                <div className={clsx(classes.paginationButtons)}>
                                    <Tooltip title="Previous page">
                                        <span>
                                            <Button disabled={!hasPreviousPage} variant="outlined" color="primary" onClick={goToPreviousPage}>
                                                <ArrowBackIcon size="small" />
                                            </Button>
                                        </span>
                                    </Tooltip>
                                    <Typography variant="body1">
                                        Page {page} of {totPages}
                                    </Typography>
                                    <Tooltip title="Next page">
                                        <span>
                                            <Button disabled={!hasNextPage} variant="outlined" color="primary" onClick={goToNextPage}>
                                                <ArrowForwardIcon size="small" />
                                            </Button>
                                        </span>
                                    </Tooltip>
                                </div>
                            ) : null}
                        </>
                    ) : (
                        <Box display="flex" p={2} justifyContent={'center'} mt={20}>
                            <Typography>{props.noItemsFoundMessage ? props.noItemsFoundMessage : 'No records'}</Typography>
                        </Box>
                    )}
                </div>
            );
        } else {
            return (
                <DataGridPro
                    {...props}
                    // The v5 grid's disableSelectionOnClick, which callers still pass, is not a v6 prop: v6 ignored
                    // it and a row click ticked the row on the Users grid and a trip's Users list (2026-10-30).
                    disableRowSelectionOnClick={props.disableRowSelectionOnClick ?? props.disableSelectionOnClick}
                    apiRef={props.apiRef}
                    className={props.className || classes.recordList}
                    style={{ border: props.noBorder && 'none' }}
                    pagination
                    filterMode="server"
                    paginationMode={props.paginationMode || "server"}
                    sortingMode={props.sortingMode || 'server'}
                    sortModel={sortModel}
                    onSortModelChange={!props.ignoreHeaderOrdering ? onSortModelChange : null}
                    onFilterModelChange={onFilterChange}
                    onRowSelectionModelChange={props.onRowSelectionModelChange}
                    page={page - 1}
                    pageSize={perPage}
                    pageSizeOptions={rowsPerPageOptions}
                    onPageChange={onPageChange}
                    onPageSizeChange={onPageSizeChange}
                    rowCount={total || 0}
                    columns={columns}
                    onRowClick={onRowClick}
                    rowHeight={props.rowHeight || 52}
                    getRowHeight={props.getRowHeight || null}
                    density="compact"
                    rows={filteredRows}
                    loading={loading || props.relatedLoading}
                    slots={{
                        ...components,
                        footer: AutoRefreshGridFooter,
                    }}
                    slotProps={{
                        ...props.slotProps,
                        pagination: {
                            ...(props.slotProps ? props.slotProps.pagination : null),
                            ...(totalIsEstimate ? {
                                labelDisplayedRows: ({ from, to }) => (
                                    <span style={{ whiteSpace: 'nowrap' }}>
                                        {`${from}–${to} of `}
                                        <PendingCountDots />
                                    </span>
                                ),
                            } : null),
                        },
                        footer: {
                            deps: refreshTrigger,
                            intervals: props.intervals,
                            autorefresh: props.autorefresh,
                            onTick: props.onAutoRefreshTick,
                        }
                    }}
                    onEditCellChangeCommitted={onEditCellChangeCommitted}
                    initialState={gridInitialState}
                    onPaginationModelChange={onPaginationModelChange}
                    headerHeight={props.headerHeight != null ? props.headerHeight : 56}
                    sx={{
                        '&.MuiDataGrid-root': {
                            border: props.borderDisplay,
                        },
                        '& .MuiDataGrid-columnHeaders': {
                            display: props.headerDisplay,
                        },
                        '& .MuiDataGrid-cell': {
                            border: props.borderDisplay,
                        },
                        '& .MuiDataGrid-row:last-child': {
                        },
                        '& .MuiDataGrid-virtualScrollerContent': {
                            paddingBottom: props.isExporting ? `${400 + perPage * 4}px` : null,
                            marginTop: props.isExporting ? 5 : 0,
                        },
                        '& .MuiDataGrid-footerContainer': {
                            //display: props.isExporting ? "none" : null,
                            // move the footer to the top when exporting to make it clear how many are being exported
                            position: props.isExporting ? 'absolute' : null,
                            top: props.isExporting ? 0 : null,
                            fontStyle: props.isExporting ? "italic" : null
                        },
                        '& .MuiTablePagination-displayedRows:before': {
                            content: props.isExporting ? '"Message history records "' : null,
                        },
                        '& .MuiDataGrid-toolbarContainer': {
                            display: props.isExporting ? "none" : null
                        },
                        '& .MuiTablePagination-actions': {
                            display: props.isExporting ? "none" : null
                        },
                        '& .MuiTablePagination-input': {
                            display: props.isExporting ? "none" : null
                        },
                        '& .MuiTablePagination-selectLabel': {
                            display: props.isExporting ? "none" : null
                        },
                    }}
                />
            );
        }
    }
});

RecordListPro.propTypes = {
    apiFilters: PropTypes.array,
    apiRef: PropTypes.object,
    autorefresh: PropTypes.bool,
    borderDisplay: PropTypes.string,
    className: PropTypes.string,
    columns: PropTypes.array,
    convert: PropTypes.bool,
    createNewButton: PropTypes.element,
    currentTab: PropTypes.number,
    deletedList: PropTypes.array,
    deps: PropTypes.any,
    disableListBorder: PropTypes.bool,
    disablePageUrlData: PropTypes.bool,
    getRowHeight: PropTypes.func,
    headerDisplay: PropTypes.string,
    headerHeight: PropTypes.number,
    hiddenAPIFilters: PropTypes.array,
    extraQueryParams: PropTypes.object,
    ignoreHeaderOrdering: PropTypes.bool,
    initialState: PropTypes.object,
    intervals: PropTypes.array,
    isExporting: PropTypes.bool,
    limitHeightForMobile: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    mobileRowComponent: PropTypes.elementType,
    noMatchesLabel: PropTypes.string,
    noBorder: PropTypes.bool,
    noItemsFoundMessage: PropTypes.string,
    onAutoRefreshTick: PropTypes.func,
    onEditCellChangeCommitted: PropTypes.func,
    onRowClick: PropTypes.func,
    onRowOrderUpdate: PropTypes.func,
    onRowSelectionModelChange: PropTypes.func,
    /** Called with the filter payload the list request actually sends (the
     *  grid's own column filters plus `apiFilters`). Bulk actions derived from
     *  this grid must use it, or they resolve a different row set than the one
     *  on screen. */
    onEffectiveFiltersChange: PropTypes.func,
    onRowsChanged: PropTypes.func,
    orderingField: PropTypes.string,
    page: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    pageReset: PropTypes.bool,
    perPage: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    query: PropTypes.string,
    relatedLoading: PropTypes.bool,
    onListFetchComplete: PropTypes.func,
    resource: PropTypes.string,
    roleCellBackgroundColor: PropTypes.object,
    rowHeight: PropTypes.number,
    rowsPerPageOptions: PropTypes.array,
    setIsLoading: PropTypes.func,
    setLastDataGridPage: PropTypes.func,
    setLastDataGridPerPage: PropTypes.func,
    slots: PropTypes.object,
    slotProps: PropTypes.object,
    sortingMode: PropTypes.string,
    sortModel: PropTypes.array,
    onSortModelChange: PropTypes.func,
    staticOptions: PropTypes.array,
    toolbar: PropTypes.elementType,
    transformRows: PropTypes.func,
    urlSuffix: PropTypes.string,
    rows: PropTypes.array,
    onRemoveUserFromTrip: PropTypes.func,
    onDeleteUser: PropTypes.func,
    showRoleChips: PropTypes.bool,
    skipDeleteConfirmation: PropTypes.bool,
    searchFilterList: PropTypes.array,
    recordListFilters: PropTypes.object,
    trip: PropTypes.object,
    setTourUserId: PropTypes.func,
    paginationMode: PropTypes.string,
};

RecordListPro.displayName = 'RecordListPro';

export default RecordListPro;
